import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/dateFormat';
import { 
  FileText, Video, BookOpen, FileSpreadsheet, Plus, Search, 
  Edit, Trash2, Eye, BarChart3, X, ExternalLink,
  Clock, TrendingUp, Upload, File, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';

const CONTENT_TYPES = [
  { value: 'article', label: 'Article', icon: FileText },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'guide', label: 'Guide', icon: BookOpen },
  { value: 'worksheet', label: 'Worksheet', icon: FileSpreadsheet },
];

function AdminContentLibrary() {
  const { user, logout } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [tags, setTags] = useState({ fearClusters: [], archetypes: [], topics: [] });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'article',
    content: '',
    thumbnailUrl: '',
    externalUrl: '',
    duration: '',
    readTime: '',
    status: 'draft',
    tags: { fearClusters: [], archetypes: [], topics: [] }
  });

  useEffect(() => {
    fetchContent();
    fetchTags();
    fetchAnalytics();
  }, [statusFilter, typeFilter]);

  const fetchContent = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('contentType', typeFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`/api/admin/content?${params.toString()}`);
      setContent(response.data.content);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get('/api/admin/content/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get('/api/admin/content/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContent();
  };

  const handleFileUpload = async (e, targetField) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post('/api/admin/content/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      // Update the appropriate field
      if (targetField === 'thumbnail') {
        setFormData(prev => ({ ...prev, thumbnailUrl: response.data.url }));
        toast.success('Thumbnail uploaded');
      } else if (targetField === 'external') {
        setFormData(prev => ({ ...prev, externalUrl: response.data.url }));
        toast.success('File uploaded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateContent = async () => {
    try {
      await axios.post('/api/admin/content', formData);
      toast.success('Content created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchContent();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create content');
    }
  };

  const handleUpdateContent = async () => {
    try {
      await axios.put(`/api/admin/content/${selectedContent.id}`, formData);
      toast.success('Content updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchContent();
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const handleDeleteContent = async () => {
    try {
      await axios.delete(`/api/admin/content/${selectedContent.id}`);
      toast.success('Content deleted successfully');
      setShowDeleteDialog(false);
      setSelectedContent(null);
      fetchContent();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const openEditDialog = (item) => {
    setSelectedContent(item);
    setFormData({
      title: item.title,
      description: item.description,
      contentType: item.contentType,
      content: item.content,
      thumbnailUrl: item.thumbnailUrl || '',
      externalUrl: item.externalUrl || '',
      duration: item.duration || '',
      readTime: item.readTime || '',
      status: item.status,
      tags: item.tags || { fearClusters: [], archetypes: [], topics: [] }
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      contentType: 'article',
      content: '',
      thumbnailUrl: '',
      externalUrl: '',
      duration: '',
      readTime: '',
      status: 'draft',
      tags: { fearClusters: [], archetypes: [], topics: [] }
    });
    setSelectedContent(null);
  };

  const toggleTag = (category, tag) => {
    setFormData(prev => {
      const currentTags = prev.tags[category] || [];
      const newTags = currentTags.includes(tag)
        ? currentTags.filter(t => t !== tag)
        : [...currentTags, tag];
      return {
        ...prev,
        tags: { ...prev.tags, [category]: newTags }
      };
    });
  };

  const getContentIcon = (type) => {
    const typeConfig = CONTENT_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : FileText;
  };

  const renderContentForm = (isEdit = false) => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Title *</label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Content title"
            data-testid="content-title-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Description *</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description"
            rows={2}
            data-testid="content-description-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Type *</label>
            <Select 
              value={formData.contentType} 
              onValueChange={(v) => setFormData({ ...formData, contentType: v })}
            >
              <SelectTrigger data-testid="content-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <Select 
              value={formData.status} 
              onValueChange={(v) => setFormData({ ...formData, status: v })}
            >
              <SelectTrigger data-testid="content-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            {formData.contentType === 'video' ? 'Video URL / Embed Code' : 'Content (Markdown)'}
          </label>
          <Textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder={formData.contentType === 'video' ? 'YouTube/Vimeo URL or embed code' : 'Write content in markdown...'}
            rows={6}
            className="font-mono text-sm"
            data-testid="content-body-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Thumbnail</label>
            <div className="flex gap-2">
              <Input
                value={formData.thumbnailUrl}
                onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                placeholder="URL or upload"
                className="flex-1"
              />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id="thumbnail-upload"
                onChange={(e) => handleFileUpload(e, 'thumbnail')}
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => document.getElementById('thumbnail-upload').click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4" />
              </Button>
            </div>
            {formData.thumbnailUrl && (
              <div className="mt-2 relative w-20 h-20 rounded overflow-hidden bg-muted">
                <img src={formData.thumbnailUrl} alt="Thumbnail" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">File/External Link</label>
            <div className="flex gap-2">
              <Input
                value={formData.externalUrl}
                onChange={(e) => setFormData({ ...formData, externalUrl: e.target.value })}
                placeholder="URL or upload PDF"
                className="flex-1"
              />
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xlsx,.pptx"
                className="hidden"
                id="file-upload"
                onChange={(e) => handleFileUpload(e, 'external')}
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => document.getElementById('file-upload').click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4" />
              </Button>
            </div>
            {formData.externalUrl && (
              <p className="text-xs text-muted-foreground mt-1 truncate">{formData.externalUrl}</p>
            )}
          </div>
        </div>
        
        {uploading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent"></div>
              Uploading... {uploadProgress}%
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Duration (videos)</label>
            <Input
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              placeholder="e.g., 5:30"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Read Time (articles)</label>
            <Input
              value={formData.readTime}
              onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
              placeholder="e.g., 5 min read"
            />
          </div>
        </div>
      </div>
      
      {/* Tags */}
      <div className="space-y-4 border-t pt-4">
        <h4 className="font-medium">Tags</h4>
        
        <div>
          <label className="block text-sm font-medium mb-2">Edge Patterns</label>
          <div className="flex flex-wrap gap-2">
            {tags.fearClusters.map(tag => (
              <Badge
                key={tag}
                variant={formData.tags.fearClusters?.includes(tag) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleTag('fearClusters', tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Core Archetypes</label>
          <div className="flex flex-wrap gap-2">
            {tags.archetypes.map(tag => (
              <Badge
                key={tag}
                variant={formData.tags.archetypes?.includes(tag) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleTag('archetypes', tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Topics</label>
          <div className="flex flex-wrap gap-2">
            {tags.topics.map(tag => (
              <Badge
                key={tag}
                variant={formData.tags.topics?.includes(tag) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleTag('topics', tag)}
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={() => isEdit ? setShowEditDialog(false) : setShowCreateDialog(false)}>
          Cancel
        </Button>
        <Button onClick={isEdit ? handleUpdateContent : handleCreateContent} data-testid="save-content-btn">
          {isEdit ? 'Save Changes' : 'Create Content'}
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-content-library">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" />
            Content Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage articles, videos, and resources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAnalyticsDialog(true)}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="add-content-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Content
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Content</p>
              <p className="text-2xl font-semibold">{analytics.totalContent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Published</p>
              <p className="text-2xl font-semibold text-green-600">{analytics.publishedContent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Drafts</p>
              <p className="text-2xl font-semibold text-amber-600">{analytics.draftContent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Views</p>
              <p className="text-2xl font-semibold text-blue-600">{analytics.totalViews}</p>
            </CardContent>
          </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Articles</p>
                <p className="text-2xl font-semibold">{analytics.byType?.article || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
            <Input
              placeholder="Search content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-xs"
            />
            <Button type="submit" variant="outline">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {CONTENT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : content.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No content yet</h3>
            <p className="text-muted-foreground mb-4">Start building your content library</p>
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Content
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {content.map((item) => {
              const Icon = getContentIcon(item.contentType);
              return (
                <Card key={item.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`content-card-${item.id}`}>
                  {item.thumbnailUrl && (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img 
                        src={item.thumbnailUrl} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground capitalize">{item.contentType}</span>
                      </div>
                      <Badge variant={item.status === 'published' ? 'default' : 'secondary'}>
                        {item.status}
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold mb-1 line-clamp-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
                    
                    {/* Tags preview */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.tags?.fearClusters?.slice(0, 2).map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                      {item.tags?.archetypes?.slice(0, 1).map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {item.viewCount || 0} views
                      </span>
                      {item.readTime && <span>{item.readTime}</span>}
                      {item.duration && <span>{item.duration}</span>}
                    </div>
                    
                    <div className="flex gap-2 mt-4 pt-3 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(item)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedContent(item); setShowDeleteDialog(true); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      {item.status === 'published' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(`/library/${item.id}`, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Content</DialogTitle>
          </DialogHeader>
          {renderContentForm(false)}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
          </DialogHeader>
          {renderContentForm(true)}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Content</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete &ldquo;{selectedContent?.title}&rdquo;? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteContent}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Content Analytics</DialogTitle>
          </DialogHeader>
          {analytics && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-3">Most Viewed Content</h4>
                <div className="space-y-2">
                  {analytics.mostViewed?.map((item, index) => (
                    <div key={item.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground">{index + 1}.</span>
                        <span className="text-sm">{item.title}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{item.viewCount} views</span>
                    </div>
                  ))}
                  {(!analytics.mostViewed || analytics.mostViewed.length === 0) && (
                    <p className="text-sm text-muted-foreground">No content viewed yet</p>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-3">Recent Access</h4>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {analytics.recentAccess?.map((access) => (
                    <div key={access.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                      <div>
                        <span className="font-medium">{access.contentTitle}</span>
                        {access.userEmail && <span className="text-muted-foreground ml-2">by {access.userEmail}</span>}
                      </div>
                      <span className="text-muted-foreground">
                        {formatDateTime(access.accessedAt)}
                      </span>
                    </div>
                  ))}
                  {(!analytics.recentAccess || analytics.recentAccess.length === 0) && (
                    <p className="text-sm text-muted-foreground">No access logs yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminContentLibrary;
